import React from "react";
import { PRICING_PLANS } from "../constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ArrowRight, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { toast } from "sonner";
import { motion } from "motion/react";

export function PricingPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [loadingPlan, setLoadingPlan] = React.useState<string | null>(null);

  const handleSelectPlan = async (planId: string) => {
    if (!user) {
      navigate("/register");
      return;
    }

    setLoadingPlan(planId);
    try {
      const now = new Date();
      const expiry = new Date();
      expiry.setMonth(now.getMonth() + 1);

      await updateDoc(doc(db, "users", user.uid), {
        subscription: {
          tier: planId,
          status: "active",
          startDate: now.toISOString(),
          expiryDate: expiry.toISOString()
        }
      });

      toast.success(`Plan ${planId.toUpperCase()} activado correctamente`);
      navigate("/become-seller");
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error("Hubo un problema al activar el plan");
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-20 px-4">
      <div className="max-w-7xl mx-auto text-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20 border-none px-4 py-1">
            Precios Flexibles
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            Selecciona tu plan y lanza tu tienda
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            La única herramienta que necesitas para vender tus productos y servicios en línea. 
            Empieza gratis y escala cuando estés listo.
          </p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
        {PRICING_PLANS.map((plan, index) => {
          const isCurrentPlan = profile?.subscription?.tier === plan.id;
          
          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className={`relative flex flex-col h-full border-none shadow-xl ${plan.color} overflow-hidden group hover:scale-105 transition-all duration-300`}>
                {plan.id === "webinar" && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-primary text-primary-foreground text-[10px] uppercase font-bold px-10 py-1 rotate-45 translate-x-10 translate-y-2">
                      Popular
                    </div>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground">/mes</span>
                  </div>
                  <CardDescription className="mt-4">
                    Ideal para {plan.id === 'free' ? 'probar la plataforma' : plan.id === 'startup' ? 'emprendedores que empiezan' : plan.id === 'webinar' ? 'negocios en crecimiento' : 'empresas consolidadas'}.
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1">
                  <ul className="space-y-4">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <div className="bg-primary/10 rounded-full p-1 mt-0.5">
                          <Check className="h-4 w-4 text-primary" />
                        </div>
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button 
                    className="w-full h-12 text-lg font-semibold group"
                    variant={isCurrentPlan ? "outline" : "default"}
                    disabled={isCurrentPlan || loadingPlan === plan.id}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {loadingPlan === plan.id ? "Activando..." :isCurrentPlan ? "Plan Actual" : plan.buttonText}
                    {!isCurrentPlan && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-24 max-w-4xl mx-auto text-center border rounded-3xl bg-white p-12 shadow-sm">
        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-6">
          <Zap className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-3xl font-bold mb-4">¿Necesitas una solución a medida?</h2>
        <p className="text-muted-foreground mb-8 text-lg">
          Estamos aquí para ayudar a los negocios más grandes a escalar. Si facturas más de $10,000 al mes, 
          podemos ofrecerte un plan personalizado con soporte 24/7.
        </p>
        <Button variant="outline" size="lg">Contactar con Ventas</Button>
      </div>
    </div>
  );
}

// Helper component for Badge which was not explicitly imported but often used in such mockups
function Badge({ children, className }: { children: React.ReactNode, className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}>
      {children}
    </span>
  );
}
